import { createSlice } from '@reduxjs/toolkit';

// Initialize state from localStorage
const loadPersistedState = () => {
  try {
    // Check if localStorage is available
    if (typeof Storage === 'undefined') {
      console.warn('localStorage is not available');
      return null;
    }
    
    const savedState = localStorage.getItem('backgroundState');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      
      // Validate the structure and version
      if (parsed && typeof parsed === 'object') {
        // Check version compatibility (for future migrations)
        if (parsed.version && parsed.version !== '1.0.0') {
          console.warn('Background state version mismatch, using defaults');
          return null;
        }
        
        // Validate background structure
        if (parsed.background && typeof parsed.background === 'object') {
          const validTypes = ['color', 'gradient', 'image', 'preset'];
          if (!validTypes.includes(parsed.background.type)) {
            console.warn('Invalid background type, using defaults');
            return null;
          }
        }
        
        return {
          ...parsed,
          // Ensure all required properties exist with proper types
          background: {
            type: parsed.background?.type || 'color',
            color: parsed.background?.color || '#FFFFFF',
            gradient: parsed.background?.gradient || null,
            imageUrl: parsed.background?.imageUrl || null,
            imageFile: null, // Never persisted
            preset: parsed.background?.preset || null
          },
          showBackgroundCustomizer: Boolean(parsed.showBackgroundCustomizer),
          history: Array.isArray(parsed.history) ? parsed.history : [],
          maxHistorySize: typeof parsed.maxHistorySize === 'number' ? 
            Math.max(1, Math.min(10, parsed.maxHistorySize)) : 3,
          settings: {
            autoSave: parsed.settings?.autoSave !== false, // Default true
            compressionQuality: typeof parsed.settings?.compressionQuality === 'number' ? 
              Math.max(0.1, Math.min(1.0, parsed.settings.compressionQuality)) : 0.8,
            maxImageSize: typeof parsed.settings?.maxImageSize === 'number' ? 
              Math.max(1024 * 1024, Math.min(20 * 1024 * 1024, parsed.settings.maxImageSize)) : 
              5 * 1024 * 1024
          },
          // Don't persist loading/error states
          isLoading: false,
          error: null,
          uploadProgress: 0
        };
      }
    }
  } catch (error) {
    console.warn('Failed to load persisted background state:', error);
    
    // Handle different error types
    if (error instanceof SyntaxError) {
      console.warn('Background state data is corrupted (invalid JSON)');
    } else if (error.name === 'SecurityError') {
      console.warn('Cannot access localStorage (private browsing mode?)');
    }
    
    // Clear corrupted state
    try {
      localStorage.removeItem('backgroundState');
    } catch (clearError) {
      console.warn('Failed to clear corrupted background state:', clearError);
    }
  }
  return null;
};

const persistedState = loadPersistedState();

const defaultState = {
  // Background configuration
  background: {
    type: 'color', // 'color' | 'gradient' | 'image' | 'preset'
    color: '#FFFFFF', // Default beige
    gradient: null,
    imageUrl: null,
    imageFile: null, // Not persisted
    preset: null
  },
  
  // Backend reference (for persistence)
  selectedBackgroundId: null, // ID of the selected background from backend
  
  // UI state
  showBackgroundCustomizer: false,
  
  // History management
  history: [], // Array of previous background states
  maxHistorySize: 3, // Maximum number of backgrounds to keep in history
  
  // Loading and error states
  isLoading: false,
  uploadProgress: 0,
  error: null,
  
  // Settings
  settings: {
    autoSave: true,
    compressionQuality: 0.8,
    maxImageSize: 5 * 1024 * 1024 // 5MB
  }
};

// Merge persisted state with default state to ensure all properties exist
const initialState = persistedState ? {
  ...defaultState,
  ...persistedState,
  // Ensure nested objects are properly merged
  background: {
    ...defaultState.background,
    ...(persistedState.background || {})
  },
  settings: {
    ...defaultState.settings,
    ...(persistedState.settings || {})
  }
} : defaultState;

export const backgroundSlice = createSlice({
  name: 'background',
  initialState,
  reducers: {
    // Background management
    setBackground: (state, action) => {
      const { background } = action.payload;
      
      // Add current background to history if it exists and is different
      if (state.background && JSON.stringify(state.background) !== JSON.stringify(background)) {
        const historyItem = {
          id: Date.now(),
          background: { ...state.background },
          timestamp: Date.now()
        };
        
        // Add to beginning of history array
        state.history.unshift(historyItem);
        
        // Maintain history size limit
        if (state.history.length > state.maxHistorySize) {
          state.history = state.history.slice(0, state.maxHistorySize);
        }
      }
      
      state.background = { ...state.background, ...background };
      state.error = null;
    },
    
    setBackgroundColor: (state, action) => {
      const color = action.payload;
      
      // Add current background to history if different
      if (state.background.type !== 'color' || state.background.color !== color) {
        const historyItem = {
          id: Date.now(),
          background: { ...state.background },
          timestamp: Date.now()
        };
        
        state.history.unshift(historyItem);
        if (state.history.length > state.maxHistorySize) {
          state.history = state.history.slice(0, state.maxHistorySize);
        }
      }
      
      state.background = {
        type: 'color',
        color,
        gradient: null,
        imageUrl: null,
        imageFile: null,
        preset: null
      };
      state.error = null;
    },
    
    setBackgroundGradient: (state, action) => {
      const gradient = action.payload;
      
      // Add current background to history if different
      if (state.background.type !== 'gradient' || state.background.gradient !== gradient) {
        const historyItem = {
          id: Date.now(),
          background: { ...state.background },
          timestamp: Date.now()
        };
        
        state.history.unshift(historyItem);
        if (state.history.length > state.maxHistorySize) {
          state.history = state.history.slice(0, state.maxHistorySize);
        }
      }
      
      state.background = {
        type: 'gradient',
        color: null,
        gradient,
        imageUrl: null,
        imageFile: null,
        preset: null
      };
      state.error = null;
    },
    
    setBackgroundImage: (state, action) => {
      const { imageUrl, imageFile, backgroundId } = action.payload;
      
      // Add current background to history if different
      if (state.background.type !== 'image' || state.background.imageUrl !== imageUrl) {
        const historyItem = {
          id: Date.now(),
          background: { ...state.background },
          timestamp: Date.now()
        };
        
        state.history.unshift(historyItem);
        if (state.history.length > state.maxHistorySize) {
          state.history = state.history.slice(0, state.maxHistorySize);
        }
      }
      
      state.background = {
        type: 'image',
        color: null,
        gradient: null,
        imageUrl,
        imageFile, // This won't be persisted
        preset: null
      };
      state.selectedBackgroundId = backgroundId || null;
      state.error = null;
    },
    
    setSelectedBackgroundId: (state, action) => {
      state.selectedBackgroundId = action.payload;
    },
    
    setBackgroundPreset: (state, action) => {
      const preset = action.payload;
      
      // Add current background to history if different
      if (state.background.type !== 'preset' || JSON.stringify(state.background.preset) !== JSON.stringify(preset)) {
        const historyItem = {
          id: Date.now(),
          background: { ...state.background },
          timestamp: Date.now()
        };
        
        state.history.unshift(historyItem);
        if (state.history.length > state.maxHistorySize) {
          state.history = state.history.slice(0, state.maxHistorySize);
        }
      }
      
      state.background = {
        type: 'preset',
        color: null,
        gradient: null,
        imageUrl: null,
        imageFile: null,
        preset
      };
      state.error = null;
    },
    
    clearBackground: (state) => {
      // Add current background to history
      if (state.background.type !== 'color' || state.background.color !== '#FFFFFF') {
        const historyItem = {
          id: Date.now(),
          background: { ...state.background },
          timestamp: Date.now()
        };
        
        state.history.unshift(historyItem);
        if (state.history.length > state.maxHistorySize) {
          state.history = state.history.slice(0, state.maxHistorySize);
        }
      }
      
      state.background = {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
        imageFile: null,
        preset: null
      };
      state.error = null;
    },
    
    // UI state management
    toggleBackgroundCustomizer: (state) => {
      state.showBackgroundCustomizer = !state.showBackgroundCustomizer;
    },
    
    setShowBackgroundCustomizer: (state, action) => {
      state.showBackgroundCustomizer = action.payload;
    },
    
    // History management
    undoBackground: (state) => {
      if (state.history.length > 0) {
        const previousBackground = state.history[0];
        
        // Remove from history
        state.history = state.history.slice(1);
        
        // Set as current background
        state.background = { ...previousBackground.background };
        state.error = null;
      }
    },
    
    addToHistory: (state, action) => {
      const { background } = action.payload;
      
      const historyItem = {
        id: Date.now(),
        background: { ...background },
        timestamp: Date.now()
      };
      
      // Add to beginning of history array
      state.history.unshift(historyItem);
      
      // Maintain history size limit
      if (state.history.length > state.maxHistorySize) {
        state.history = state.history.slice(0, state.maxHistorySize);
      }
    },
    
    clearHistory: (state) => {
      state.history = [];
    },
    
    setMaxHistorySize: (state, action) => {
      const newSize = Math.max(1, Math.min(10, action.payload)); // Limit between 1-10
      state.maxHistorySize = newSize;
      
      // Trim history if it exceeds new size
      if (state.history.length > newSize) {
        state.history = state.history.slice(0, newSize);
      }
    },
    
    // Loading and error states
    setLoading: (state, action) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    
    setUploadProgress: (state, action) => {
      state.uploadProgress = Math.max(0, Math.min(100, action.payload));
    },
    
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
      state.uploadProgress = 0;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // Settings
    updateSettings: (state, action) => {
      state.settings = {
        ...state.settings,
        ...action.payload
      };
    },
    
    // Batch updates for performance
    batchUpdate: (state, action) => {
      const updates = action.payload;
      Object.keys(updates).forEach(key => {
        if (key in state) {
          state[key] = updates[key];
        }
      });
    },
    
    // Reset to initial state
    resetBackgroundState: (state) => {
      Object.assign(state, {
        ...initialState,
        history: state.history || [], // Preserve history
        maxHistorySize: state.maxHistorySize || 3 // Preserve history size setting
      });
    }
  }
});

export const {
  setBackground,
  setBackgroundColor,
  setBackgroundGradient,
  setBackgroundImage,
  setBackgroundPreset,
  setSelectedBackgroundId,
  clearBackground,
  toggleBackgroundCustomizer,
  setShowBackgroundCustomizer,
  undoBackground,
  addToHistory,
  clearHistory,
  setMaxHistorySize,
  setLoading,
  setUploadProgress,
  setError,
  clearError,
  updateSettings,
  batchUpdate,
  resetBackgroundState
} = backgroundSlice.actions;

// Note: Image compression removed - we now store backend URLs only, not base64 data
// Backend handles image optimization and storage

// Utility function to estimate storage size
const estimateStorageSize = (data) => {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch (error) {
    return JSON.stringify(data).length * 2; // Rough estimate
  }
};

// Persistence middleware
export const persistBackgroundState = (store) => (next) => (action) => {
  const result = next(action);
  
  // Only persist on background actions
  if (action.type.startsWith('background/')) {
    const state = store.getState().background;
    
    // Create a clean state object for persistence
    let persistState = {
      background: {
        type: state.background.type,
        color: state.background.color,
        gradient: state.background.gradient,
        imageUrl: state.background.imageUrl,
        // Don't persist imageFile - it's not serializable
        preset: state.background.preset
      },
      showBackgroundCustomizer: state.showBackgroundCustomizer,
      settings: state.settings,
      // Persist history (limit to prevent localStorage bloat)
      history: state.history.slice(0, Math.min(3, state.maxHistorySize)), // Only persist last 3 items
      maxHistorySize: state.maxHistorySize,
      timestamp: Date.now(),
      version: '1.0.0' // For future migration support
    };
    
    // Since we're now using backend URLs (not base64), no compression needed
    // Just persist the URL reference directly
    attemptPersistence(persistState);
    
    function attemptPersistence(data) {
      try {
        localStorage.setItem('backgroundState', JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to persist background state:', error);
        
        // Handle different error scenarios
        if (error.name === 'QuotaExceededError') {
          // Try progressive cleanup strategies
          handleQuotaExceeded(data);
        } else if (error.name === 'SecurityError') {
          console.warn('localStorage access denied (private browsing mode?)');
        } else {
          console.error('Unexpected persistence error:', error);
        }
      }
    }
    
    function handleQuotaExceeded(data) {
      try {
        // Strategy 1: Clear old background state and retry
        localStorage.removeItem('backgroundState');
        localStorage.setItem('backgroundState', JSON.stringify(data));
      } catch (retryError) {
        try {
          // Strategy 2: Remove history and retry
          const minimalData = {
            ...data,
            history: []
          };
          localStorage.removeItem('backgroundState');
          localStorage.setItem('backgroundState', JSON.stringify(minimalData));
        } catch (finalError) {
          try {
            // Strategy 3: Save only essential background info
            const essentialData = {
              background: {
                type: data.background.type,
                color: data.background.color,
                gradient: data.background.gradient,
                // Remove image data if present
                imageUrl: data.background.type === 'image' ? null : data.background.imageUrl,
                preset: data.background.preset
              },
              settings: data.settings,
              timestamp: Date.now(),
              version: '1.0.0'
            };
            localStorage.removeItem('backgroundState');
            localStorage.setItem('backgroundState', JSON.stringify(essentialData));
          } catch (ultimateError) {
            console.error('All persistence strategies failed:', ultimateError);
            // Continue with in-memory state only
          }
        }
      }
    }
  }
  
  return result;
};

export default backgroundSlice.reducer;